"use client"

import { useState, useEffect, Activity, useTransition } from "react"
import Cookies from "js-cookie"
import { getT } from "@local/i18n/src/t"
import {
  getLanguageForMarket,
  SUPPORTED_LANGUAGES,
} from "@local/i18n/src/languages"
import { preferredLanguageFromTags } from "@local/i18n/src/accept-language"
import { countryNames } from "@local/i18n/src/catalogs/countryNames"
import { marketSelector } from "@local/i18n/src/catalogs/marketSelector"
import { I18n } from "../../../lib/i18n"
import { CloseIcon } from "@local/ui/src/components/ui/icons"
import { useRouter } from "next/navigation"
import { Flag } from "./flags"
import type { Route } from "next"

interface MarketModalProps {
  currentCountry: string
  detectedCountry: string
  startVisible?: boolean
}

export const MarketModal = ({
  currentCountry,
  detectedCountry,
  startVisible = false,
}: MarketModalProps) => {
  const [isVisible, setIsVisible] = useState(startVisible)
  const { push } = useRouter()
  const [isPending, startTransition] = useTransition()

  useEffect(() => {
    // Check if user has already made a choice
    const overrideStore = Cookies.get("override_store")

    // Only show popup if countries don't match and no override cookie exists
    if (
      currentCountry.toLowerCase() !== detectedCountry.toLowerCase() &&
      !overrideStore
    ) {
      startTransition(() => {
        setIsVisible(true)
      })
    }
  }, [currentCountry, detectedCountry])

  const handleGoToDetectedCountry = () => {
    Cookies.set("override_store", detectedCountry.toLowerCase())

    // Get current URL and replace domain/path for country
    const url = I18n.buildMarketUrl(
      window.location.href,
      currentCountry.toLowerCase(),
      detectedCountry.toLowerCase()
    )

    // Redirect to detected country store
    push(url.href as Route)
  }

  const handleContinueCurrentCountry = () => {
    Cookies.set("override_store", currentCountry.toLowerCase())
    startTransition(() => {
      setIsVisible(false)
    })
  }

  const detectedCountryData = I18n.getMarketData(detectedCountry)
  const currentCountryData = I18n.getMarketData(currentCountry)

  // The modal addresses a shopper who may not read the page's language (that
  // is the premise of offering another market), so it speaks the browser's
  // preferred supported language and falls back to the page market's language.
  // Reading `navigator` here is hydration-safe: the modal renders null on the
  // server and on the first client render (visibility flips in the effect
  // above), so server and hydration output never disagree.
  const browserLanguage = preferredLanguageFromTags(
    typeof navigator === "undefined"
      ? undefined
      : (navigator.languages ?? [navigator.language]),
    SUPPORTED_LANGUAGES
  )
  const t = getT(browserLanguage ?? getLanguageForMarket(currentCountry), {
    countryNames,
    marketSelector,
  })

  // Localized market/country name by slug (`countryNames.*`), falling back to
  // the English-only `marketsData.name` for any slug missing a catalog entry.
  const localizedCountryName = (slug: string, fallback: string): string => {
    const localized = t(`countryNames.${slug}` as Parameters<typeof t>[0])
    return localized === `countryNames.${slug}` ? fallback : localized
  }
  const currentName = localizedCountryName(
    currentCountry,
    currentCountryData.name
  )
  const detectedName = localizedCountryName(
    detectedCountry,
    detectedCountryData.name
  )

  if (!isVisible || isPending) return null

  return (
    <Activity mode={isVisible ? "visible" : "hidden"}>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="market-selector-heading"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        {/* Modal */}
        <div className="relative mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-black shadow-2xl">
          {/* Close button */}
          <button
            type="button"
            onClick={handleContinueCurrentCountry}
            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-none bg-transparent transition-colors hover:bg-gray-100"
            aria-label={t("marketSelector.closeAria")}
          >
            <CloseIcon className="h-5 w-5 text-white" />
          </button>

          {/* Content */}
          <div className="flex min-h-96 flex-col justify-center p-8 px-6 py-4 text-white">
            {/* Main message */}
            <div className="mb-8 text-center">
              <h3
                id="market-selector-heading"
                className="text-h3 leading-heading-tight mb-4 flex flex-wrap items-center justify-center gap-2 font-semibold text-balance text-amber-400 uppercase"
              >
                <Flag
                  country={currentCountryData.market}
                  className="h-4 w-6 shrink-0"
                  width={24}
                  height={18}
                />
                {t("marketSelector.heading", { country: currentName })}
              </h3>
              <p className="flex flex-wrap items-center justify-center gap-2 text-sm text-balance text-gray-300 uppercase">
                {t("marketSelector.detected")}
                <Flag
                  country={detectedCountryData.market}
                  className="h-3 w-4 shrink-0"
                  width={16}
                  height={12}
                />
                {detectedName}
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleGoToDetectedCountry}
                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-amber-400 px-6 py-4 text-center text-lg font-bold text-balance text-black uppercase transition-colors hover:bg-amber-500"
              >
                <Flag
                  country={detectedCountryData.market}
                  className="h-4 w-5 shrink-0"
                  width={20}
                  height={15}
                />
                {t("marketSelector.goTo", { country: detectedName })}
              </button>

              <button
                type="button"
                onClick={handleContinueCurrentCountry}
                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-amber-400 bg-transparent px-4 py-3 text-center text-base font-bold text-balance text-amber-400 uppercase transition-colors hover:bg-amber-400 hover:text-black"
              >
                <Flag
                  country={currentCountryData.market}
                  className="h-3 w-4 shrink-0"
                  width={16}
                  height={12}
                />
                {t("marketSelector.continueOn", {
                  country: currentName,
                })}
              </button>
            </div>

            {/* Shipping restriction footnote */}
            <p className="mt-4 text-center text-xs leading-relaxed text-gray-400">
              {t("marketSelector.shippingNote")}
            </p>
          </div>
        </div>
      </div>
    </Activity>
  )
}
